/**
 * AskClientButton — "Preguntar al cliente": abre un popover con un mensaje
 * pre-redactado que referencia el campo específico y lo envía por WhatsApp.
 *
 * Decisiones de diseño:
 * - Popover inline (no modal): preguntar al cliente NO es una acción terminal;
 *   los modales quedan reservados para Generar Anexo y Descartar (principio 7).
 * - El mensaje llega pre-redactado (defaultMessage) pero editable: el operador
 *   ajusta el tono sin redactar desde cero — presupuesto de 3 encomiendas/hora.
 * - Panel de redacción, no tooltip: mínimo 400×120px, textarea que crece con el
 *   contenido hasta 240px; flota bajo el botón para no tapar el campo referido.
 * - onSend puede devolver una promesa: se muestra 'Enviando…' y, si falla, un
 *   error inline SIN cerrar el popover — el operador no pierde su texto.
 * - Esc cierra (listener solo mientras está abierto). Botón primario en rojo
 *   de marca con min-h 44px (principio de área de toque).
 * - Variante compact (solo ícono) para filas de artículo densas; el aria-label
 *   se mantiene siempre.
 */
import { useEffect, useRef, useState } from 'react';
import { MessageCircleQuestion } from 'lucide-react';

const AskClientButton = ({ fieldLabel, defaultMessage, onSend, compact }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState(defaultMessage || '');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);
  const textareaRef = useRef(null);

  // El textarea crece con el contenido hasta 240px; después, scroll interno.
  const ajustarAltura = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 240)}px`;
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    textareaRef.current?.focus();
    ajustarAltura();
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const abrir = () => {
    setMessage(defaultMessage || '');
    setError(false);
    setOpen(true);
  };

  const enviar = async () => {
    setSending(true);
    setError(false);
    try {
      await Promise.resolve(onSend && onSend(message));
      setOpen(false);
    } catch (e) {
      setError(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={abrir}
        aria-label={`Preguntar al cliente sobre ${fieldLabel || 'este campo'}`}
        title="Preguntar al cliente"
        className={`inline-flex items-center gap-1 rounded-md text-[11px] font-medium
                    hover:bg-[var(--nav-active-bg)]
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--blue)]
                    disabled:opacity-40 disabled:cursor-not-allowed
                    ${compact ? 'p-1.5' : 'px-2 py-1'}`}
        style={{ color: 'var(--text-secondary)' }}
      >
        <MessageCircleQuestion size={13} aria-hidden="true" />
        {!compact && <span>Preguntar al cliente</span>}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={`Mensaje para el cliente — ${fieldLabel || 'campo'}`}
          className="absolute right-0 top-full z-50 mt-1 w-[400px] min-h-[120px] rounded-lg border p-3 shadow-lg"
          style={{
            background: 'var(--tile)',
            borderColor: 'var(--border)',
            boxShadow: 'var(--tile-shadow)',
          }}
        >
          <p className="mb-2 text-[11px] font-medium" style={{ color: 'var(--text-secondary)' }}>
            Mensaje para el cliente — {fieldLabel || 'campo'}
          </p>
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              ajustarAltura();
            }}
            rows={3}
            disabled={sending}
            aria-label="Texto del mensaje para el cliente"
            className="w-full min-h-[60px] max-h-[240px] resize-none overflow-y-auto rounded-md border px-2 py-1.5 text-xs
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--blue)]
                       disabled:opacity-60"
            style={{
              background: 'var(--input-bg)',
              borderColor: 'var(--border)',
              color: 'var(--text-main)',
            }}
          />
          {error && (
            <p className="mt-1 text-[11px]" style={{ color: 'var(--avianca-red)' }} role="alert">
              No se pudo enviar. Intente de nuevo.
            </p>
          )}
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={sending}
              aria-label="Cancelar mensaje"
              className="rounded-md border px-3 py-2 text-xs font-medium
                         hover:bg-[var(--nav-active-bg)]
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--blue)]
                         disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={enviar}
              disabled={sending || !message.trim()}
              aria-label="Enviar mensaje por WhatsApp"
              className="min-h-[44px] rounded-md px-3 py-2 text-xs font-semibold text-white
                         hover:opacity-90
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--avianca-red)] focus-visible:ring-offset-1
                         disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'var(--avianca-red)' }}
            >
              {sending ? 'Enviando…' : 'Enviar por WhatsApp'}
            </button>
          </div>
        </div>
      )}
    </span>
  );
};

export default AskClientButton;

/*
Ejemplo de uso (junto al campo condición de un artículo):
  <AskClientButton
    compact
    fieldLabel="condición del artículo"
    defaultMessage="Confirmame por favor: los 3 pares de zapatos, ¿son nuevos o usados?"
    onSend={(texto) => sendClientQuestion(draft.id, { field_ref: 'items[2].condition', message: texto })}
  />
*/
